/**
 * Панель страницы «Элементы»: представления, сохранённые фильтры и поиск.
 *
 * Списки значений собираются из реестра типов и данных проекта — ни один
 * фильтр не содержит захардкоженного перечня статусов.
 */

import {
  CalendarDaysIcon,
  FilterXIcon,
  KanbanIcon,
  ListIcon,
  RowsIcon,
  SearchIcon,
  Table2Icon,
} from 'lucide-react';
import { useMemo } from 'react';
import {
  ALL_ITEM_STATUSES,
  getItemTypeDefinition,
  ITEM_TYPES,
  PRIORITIES,
  PRIORITY_LABELS_RU,
  type ItemType,
  type ProjectMemberDto,
  type WorkstreamDto,
} from '@project-os/contracts';
import type { ItemQuery } from '@/api/items';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { MultiSelectFilter, type FilterOption } from './MultiSelectFilter';
import { Toggle } from '@/components/ui/toggle';
import { SAVED_VIEWS } from './saved-views';
import type { BoardGrouping } from './views/ItemsBoard';

export type ViewMode = 'table' | 'board' | 'calendar' | 'list';

const MODES = [
  { value: 'table', label: 'Таблица', icon: Table2Icon },
  { value: 'board', label: 'Доска', icon: KanbanIcon },
  { value: 'calendar', label: 'Календарь', icon: CalendarDaysIcon },
  { value: 'list', label: 'Список', icon: ListIcon },
] as const;

const GROUPINGS: { value: BoardGrouping; label: string }[] = [
  { value: 'status', label: 'По статусу' },
  { value: 'workstream', label: 'По направлению' },
  { value: 'owner', label: 'По ответственному' },
  { value: 'priority', label: 'По приоритету' },
];

export interface ItemsToolbarProps {
  query: ItemQuery;
  onQueryChange: (next: ItemQuery) => void;
  activeView: string;
  onViewChange: (viewId: string) => void;
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  grouping: BoardGrouping;
  onGroupingChange: (grouping: BoardGrouping) => void;
  singleType: ItemType | null;
  density: 'normal' | 'compact';
  onDensityChange: (density: 'normal' | 'compact') => void;
  search: string;
  onSearchChange: (value: string) => void;
  workstreams: WorkstreamDto[];
  members: ProjectMemberDto[];
  typeCounts: Record<string, number>;
}

export function ItemsToolbar({
  query,
  onQueryChange,
  activeView,
  onViewChange,
  mode,
  onModeChange,
  grouping,
  onGroupingChange,
  singleType,
  density,
  onDensityChange,
  search,
  onSearchChange,
  workstreams,
  members,
  typeCounts,
}: ItemsToolbarProps) {
  const typeOptions: FilterOption[] = useMemo(
    () =>
      ITEM_TYPES.map((type) => ({
        value: type,
        label: getItemTypeDefinition(type).labelRu,
        count: typeCounts[type] ?? 0,
      })),
    [typeCounts],
  );

  /**
   * Статусы показываются только те, что существуют у выбранных типов.
   * Если тип не выбран, показываются все.
   */
  const statusOptions: FilterOption[] = useMemo(() => {
    const selectedTypes = (query.type?.length ? query.type : ITEM_TYPES) as ItemType[];
    const seen = new Map<string, string>();

    for (const type of selectedTypes) {
      for (const status of getItemTypeDefinition(type).statuses) {
        if (!seen.has(status.value)) seen.set(status.value, status.labelRu);
      }
    }

    return [...seen.entries()]
      .filter(([value]) => ALL_ITEM_STATUSES.includes(value))
      .map(([value, label]) => ({ value, label }));
  }, [query.type]);

  const workstreamOptions: FilterOption[] = workstreams.map((workstream) => ({
    value: workstream.id,
    label: workstream.name,
  }));

  const memberOptions: FilterOption[] = members.map((member) => ({
    value: member.user.id,
    label: member.user.name,
  }));

  const priorityOptions: FilterOption[] = PRIORITIES.map((priority) => ({
    value: priority,
    label: PRIORITY_LABELS_RU[priority],
  }));

  const hasFilters = Boolean(
    query.type?.length ||
    query.status?.length ||
    query.workstreamId?.length ||
    query.ownerId?.length ||
    query.priority?.length ||
    query.isNextAction ||
    query.includeArchived ||
    search,
  );

  const update = (patch: Partial<ItemQuery>) => onQueryChange({ ...query, ...patch });

  return (
    <div className="space-y-2.5 px-6 pt-1 pb-3">
      {/* Сохранённые представления, справа — переключатели по всему списку */}
      <div className="flex flex-wrap items-center gap-0.5">
        {SAVED_VIEWS.map((view) => (
          <button
            key={view.id}
            type="button"
            title={view.description}
            onClick={() => onViewChange(view.id)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs transition-colors',
              activeView === view.id
                ? 'bg-accent font-medium text-foreground'
                : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
            )}
          >
            {view.label}
          </button>
        ))}

        <div className="flex-1" />

        {/* Плотность относится только к таблице: в доске и календаре
            высоты строки не существует. */}
        {mode === 'table' ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Toggle
                size="sm"
                className="h-7 px-2"
                pressed={density === 'compact'}
                onPressedChange={(pressed) => onDensityChange(pressed ? 'compact' : 'normal')}
                aria-label="Компактная таблица"
              >
                <RowsIcon className="size-3.5" />
              </Toggle>
            </TooltipTrigger>
            <TooltipContent>Компактная таблица</TooltipContent>
          </Tooltip>
        ) : null}

        <Button
          variant={query.includeArchived ? 'secondary' : 'ghost'}
          size="sm"
          className="h-7 text-xs"
          onClick={() => update({ includeArchived: !query.includeArchived })}
        >
          Архивные
        </Button>

        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground"
            onClick={() => {
              onSearchChange('');
              onViewChange('all');
            }}
          >
            <FilterXIcon />
            Сбросить
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Переключатель представления */}
        <div className="flex rounded-lg bg-muted/60 p-0.5">
          {MODES.map((option) => (
            <Tooltip key={option.value}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onModeChange(option.value)}
                  aria-pressed={mode === option.value}
                  aria-label={option.label}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors',
                    mode === option.value
                      ? 'bg-card text-foreground shadow-soft'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <option.icon className="size-3.5" />
                  <span className="hidden lg:inline">{option.label}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="lg:hidden">{option.label}</TooltipContent>
            </Tooltip>
          ))}
        </div>

        {mode === 'board' ? (
          <Select
            value={grouping}
            onValueChange={(value) => onGroupingChange(value as BoardGrouping)}
          >
            <SelectTrigger size="sm" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GROUPINGS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  disabled={option.value === 'status' && !singleType}
                >
                  {option.label}
                  {option.value === 'status' && !singleType ? (
                    <span className="ml-1 text-2xs text-muted-foreground">— выберите тип</span>
                  ) : null}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        <div className="relative w-52 shrink-0 xl:w-64">
          <SearchIcon
            className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Поиск по названию и описанию"
            className="h-8 pl-8"
            aria-label="Поиск по элементам"
          />
        </div>

        <MultiSelectFilter
          label="Тип"
          options={typeOptions}
          selected={query.type ?? []}
          onChange={(value) => update({ type: value })}
          showCounts
        />
        <MultiSelectFilter
          label="Статус"
          options={statusOptions}
          selected={query.status ?? []}
          onChange={(value) => update({ status: value })}
        />
        <MultiSelectFilter
          label="Направление"
          options={workstreamOptions}
          selected={query.workstreamId ?? []}
          onChange={(value) => update({ workstreamId: value })}
        />
        <MultiSelectFilter
          label="Ответственный"
          options={memberOptions}
          selected={query.ownerId ?? []}
          onChange={(value) => update({ ownerId: value })}
        />
        <MultiSelectFilter
          label="Приоритет"
          options={priorityOptions}
          selected={query.priority ?? []}
          onChange={(value) => update({ priority: value })}
        />
      </div>
    </div>
  );
}
